import type { RGB, TemplateResult } from "./types";

interface Bucket {
  pixels: Uint32Array; // packed 0xRRGGBB values
  start: number;
  end: number; // exclusive
}

function packRGB(r: number, g: number, b: number): number {
  return (r << 16) | (g << 8) | b;
}

function unpackRGB(p: number): RGB {
  return [(p >>> 16) & 0xff, (p >>> 8) & 0xff, p & 0xff];
}

function rangeOnAxis(buf: Uint32Array, start: number, end: number, shift: number): number {
  let lo = 255;
  let hi = 0;
  for (let i = start; i < end; i++) {
    const v = (buf[i] >>> shift) & 0xff;
    if (v < lo) lo = v;
    if (v > hi) hi = v;
  }
  return hi - lo;
}

function averageColor(buf: Uint32Array, start: number, end: number): RGB {
  let r = 0;
  let g = 0;
  let b = 0;
  const n = end - start;
  for (let i = start; i < end; i++) {
    const v = buf[i];
    r += (v >>> 16) & 0xff;
    g += (v >>> 8) & 0xff;
    b += v & 0xff;
  }
  return [Math.round(r / n), Math.round(g / n), Math.round(b / n)];
}

/**
 * Median-cut quantization. Returns up to `k` colors averaged from the original pixels.
 *
 * Algorithm:
 *   1. Place all pixels into one bucket.
 *   2. Repeatedly split the bucket with the largest channel range along that channel
 *      at the median, until we have `k` buckets or no bucket can be split further.
 *   3. Each bucket's representative color is the integer-rounded mean of its pixels.
 */
export function medianCut(rgbPixels: Uint32Array, k: number): RGB[] {
  if (rgbPixels.length === 0 || k <= 1) {
    return [averageColor(rgbPixels, 0, rgbPixels.length || 1)];
  }
  // Sort within buckets lazily — start with one big bucket on a mutable copy.
  const buf = new Uint32Array(rgbPixels);
  const buckets: Bucket[] = [{ pixels: buf, start: 0, end: buf.length }];

  while (buckets.length < k) {
    // Find bucket with widest range across any channel.
    let best = -1;
    let bestRange = -1;
    let bestShift = 16;
    for (let bi = 0; bi < buckets.length; bi++) {
      const { start, end } = buckets[bi];
      if (end - start < 2) continue;
      const rR = rangeOnAxis(buf, start, end, 16);
      const rG = rangeOnAxis(buf, start, end, 8);
      const rB = rangeOnAxis(buf, start, end, 0);
      const max = Math.max(rR, rG, rB);
      if (max > bestRange) {
        bestRange = max;
        best = bi;
        bestShift = max === rR ? 16 : max === rG ? 8 : 0;
      }
    }
    if (best < 0 || bestRange === 0) break;

    const { start, end } = buckets[best];
    // Sort the slice in-place by the chosen channel.
    const slice = buf.subarray(start, end);
    const arr = Array.from(slice);
    arr.sort((a, b) => ((a >>> bestShift) & 0xff) - ((b >>> bestShift) & 0xff));
    for (let i = 0; i < arr.length; i++) slice[i] = arr[i];

    const mid = start + ((end - start) >> 1);
    buckets.splice(best, 1, { pixels: buf, start, end: mid }, { pixels: buf, start: mid, end });
  }

  return buckets.map(({ start, end }) => averageColor(buf, start, end));
}

function squaredDistance(a: RGB, b: RGB): number {
  const dr = a[0] - b[0];
  const dg = a[1] - b[1];
  const db = a[2] - b[2];
  return dr * dr + dg * dg + db * db;
}

/** Map every pixel to the nearest color in `palette`. */
export function indexPixels(rgbPixels: Uint32Array, palette: RGB[]): Uint8Array {
  const out = new Uint8Array(rgbPixels.length);
  for (let i = 0; i < rgbPixels.length; i++) {
    const px = unpackRGB(rgbPixels[i]);
    let bestIdx = 0;
    let bestDist = Infinity;
    for (let p = 0; p < palette.length; p++) {
      const d = squaredDistance(px, palette[p]);
      if (d < bestDist) {
        bestDist = d;
        bestIdx = p;
      }
    }
    out[i] = bestIdx;
  }
  return out;
}

/**
 * Greedy palette merge:
 *  - Order original palette indices by descending usage so the most common color "wins".
 *  - For each color (in that order), if its RGB euclidean distance to any already-kept
 *    color is below `threshold`, remap it to that kept color; otherwise keep it.
 *  - Reindex so the output palette is dense and ordered by descending usage.
 *
 * Returns the new palette, the new index array, and the new per-color counts.
 */
export function mergeClosePalette(
  indices: Uint8Array,
  palette: RGB[],
  threshold: number
): { palette: RGB[]; indices: Uint8Array; counts: number[] } {
  const counts = new Array<number>(palette.length).fill(0);
  for (let i = 0; i < indices.length; i++) counts[indices[i]]++;

  const order = palette
    .map((_, i) => i)
    .sort((a, b) => counts[b] - counts[a]);

  const remap = new Array<number>(palette.length).fill(-1);
  const kept: number[] = []; // original indices kept, in usage order
  const thresholdSq = threshold * threshold;

  for (const i of order) {
    let mergedTo = -1;
    for (const k of kept) {
      if (squaredDistance(palette[i], palette[k]) < thresholdSq) {
        mergedTo = k;
        break;
      }
    }
    if (mergedTo === -1) {
      remap[i] = i;
      kept.push(i);
    } else {
      remap[i] = mergedTo;
    }
  }

  // Build dense final palette in usage order.
  const oldToNew = new Map<number, number>();
  kept.forEach((old, newIdx) => oldToNew.set(old, newIdx));
  const newPalette = kept.map((old) => palette[old]);
  const newIndices = new Uint8Array(indices.length);
  for (let i = 0; i < indices.length; i++) {
    newIndices[i] = oldToNew.get(remap[indices[i]])!;
  }
  const newCounts = new Array<number>(newPalette.length).fill(0);
  for (let i = 0; i < newIndices.length; i++) newCounts[newIndices[i]]++;
  return { palette: newPalette, indices: newIndices, counts: newCounts };
}

/** End-to-end: quantize ImageData (already at GRID×GRID) to a TemplateResult. */
export function buildTemplate(
  pixels: ImageData,
  paletteSize: number,
  mergeDistance: number
): TemplateResult {
  const n = pixels.width * pixels.height;
  const rgb = new Uint32Array(n);
  const data = pixels.data;
  for (let i = 0, j = 0; i < n; i++, j += 4) {
    rgb[i] = packRGB(data[j], data[j + 1], data[j + 2]);
  }
  const palette = medianCut(rgb, paletteSize);
  const indices = indexPixels(rgb, palette);
  const merged = mergeClosePalette(indices, palette, mergeDistance);
  return {
    grid: pixels.width,
    indices: merged.indices,
    palette: merged.palette,
    counts: merged.counts,
  };
}
